import * as Sentry from "@sentry/vue"
import { IDevice } from "@/androidPlugins/IDevice"
import { IPreferences } from "@/androidPlugins/IPreferences"
import { isWebViewVersionSufficient } from "@/utils/webViewVersionCheck"

const WEB_VIEW_WARNING_LAST_ACKNOWLEDGED_AT_KEY =
  "webViewWarningLastAcknowledgedAt"
const IS_INGENICO_PLATFORM =
  import.meta.env.VITE_PLATFORM === "ingenicoNerpay"

const getNextAllowedWarningTimestamp = (
  acknowledgedAt: number
) => {
  const nextAllowedDate = new Date(acknowledgedAt)
  nextAllowedDate.setMonth(nextAllowedDate.getMonth() + 1) // Note setting a value of 12 will set to January of next year, so this works.

  return nextAllowedDate.getTime()
}

const hasActiveAcknowledgementCooldown = async () => {
  const { value } = await IPreferences.get({
    key: WEB_VIEW_WARNING_LAST_ACKNOWLEDGED_AT_KEY,
  })

  if (value === null) {
    return false
  }

  const acknowledgedAt = Number(value)

  if (Number.isNaN(acknowledgedAt)) {
    return false
  }

  return (
    Date.now() <
    getNextAllowedWarningTimestamp(acknowledgedAt)
  )
}

export const useShowWebViewVersionWarning = () => {
  const detectedWebViewVersion = ref<string>()
  const showWebViewVersionWarning = ref(false)

  const acknowledgeWebViewVersionWarning = async () => {
    showWebViewVersionWarning.value = false

    await IPreferences.set({
      key: WEB_VIEW_WARNING_LAST_ACKNOWLEDGED_AT_KEY,
      value: Date.now().toString(),
    })
  }

  onMounted(async () => {
    try {
      // We don't want to show the warning on Ingenico terminals since
      //  they use a custom WebView.
      if (IS_INGENICO_PLATFORM) return

      if (await hasActiveAcknowledgementCooldown()) {
        return
      }

      const { webViewVersion } = await IDevice.getInfo()
      detectedWebViewVersion.value = webViewVersion

      if (!isWebViewVersionSufficient(webViewVersion)) {
        showWebViewVersionWarning.value = true
        Sentry.captureMessage(
          "WebView version below recommended",
          {
            level: "warning",
            extra: {
              webViewVersion,
            },
          }
        )
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  })

  return {
    detectedWebViewVersion: readonly(
      detectedWebViewVersion
    ),
    showWebViewVersionWarning: readonly(
      showWebViewVersionWarning
    ),
    acknowledgeWebViewVersionWarning,
  }
}
